import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const fetchOrders = createAsyncThunk('orders/fetchMany', async (params) => {
    const response = await api.get('/orders', { params })
    return response.data;
});

// Past orders are a separate query with their own paging, so the Past Orders
// section can be browsed all the way back without walking through every future
// screening first (the list is sorted screening-date-descending).
export const fetchPastOrders = createAsyncThunk('orders/fetchPast', async (params) => {
    const response = await api.get('/orders', { params: { ...params, timeframe: 'past' } })
    return response.data;
});

export const fetchHomepageOrders = createAsyncThunk('orders/fetchHomepage', async () => {
    const response = await api.get('/homefeed/bac/orders');
    return response.data;
});

export const fetchHomepageOrganizations = createAsyncThunk('organizations/fetchHomepage', async () => {
    const response = await api.get('/homefeed/bac/organizations');
    return response.data;
});

export const fetchOrder = createAsyncThunk('orders/fetch', async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
});

export const orderPurchase = createAsyncThunk('orders/purchase', async ({ orderId, body }, { rejectWithValue }) => {
    try {
        const response = await api.post(`/orders/${orderId}/purchase`, body);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const orderRevert = createAsyncThunk('orders/revert', async ({ orderId, body }) => {
    const response = await api.post(`/orders/${orderId}/revert`, body);
    return response.data;
});

export const amendScreeningDate = createAsyncThunk('orders/amendScreeningDate', async ({ orderId, body }) => {
    const response = await api.post(`/orders/${orderId}/amend-screening-date`, body);
    return response.data;
});

export const updateOrder = createAsyncThunk('orders/update', async ({ orderId, body }) => {
    const response = await api.put(`/orders/${orderId}`, body);
    return response.data;
});

export const requestApproval = createAsyncThunk('orders/requestApproval', async ({ orderId, operation, body }) => {
    const response = await api.put(`/orders/${orderId}/${operation}`, body);
    return response.data;
});

export const getIngestJobs = createAsyncThunk('orders/getIngestJobs', async () => {
    const response = await api.get('/orders/ingest-status');
    return response.data;
});

const orderSlice = createSlice({
    name: 'orders',
    initialState: {
        list: [],
        pagination: {
            currentPage: 1,
            totalPages: 0,
            totalCount: 0,
            limit: 50,
            hasMore: false,
        },
        pastList: [],
        pastPagination: {
            currentPage: 1,
            totalPages: 0,
            totalCount: 0,
            limit: 50,
            hasMore: false,
        },
        pastStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        homepageList: null,
        homepageOrganizations: null,
        currentOrder: null,
        status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        error: null,
        statusIngest: 'success',
        ingestJobs: [],
    },
    reducers: {
        setCurrentOrder: (state, { payload }) => {
            state.currentOrder = payload;
            state.status = 'success';
            state.error = null;
        },
        markOrderFinishedPlayback: (state, { payload: orderId }) => {
            if (state.currentOrder && state.currentOrder._id === orderId) {
                state.currentOrder.status = 'finished-playback';
                state.currentOrder.statusWorkflow = 'finished-playback';
            }
            const i = state.list.findIndex(o => o._id === orderId);
            if (i !== -1) {
                state.list[i].status = 'finished-playback';
                state.list[i].statusWorkflow = 'finished-playback';
            }
        },
        // Realtime status from playback socket events ('playing', or the true
        // post-show status — the server may finalize to 'ready-to-playback').
        setOrderRealtimeStatus: (state, { payload: { orderId, status } }) => {
            if (state.currentOrder && state.currentOrder._id === orderId) {
                state.currentOrder.status = status;
                state.currentOrder.statusWorkflow = status;
            }
            const i = state.list.findIndex(o => o._id === orderId);
            if (i !== -1) {
                state.list[i].status = status;
                state.list[i].statusWorkflow = status;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrders.pending, (state) => {
                state.status = 'loading';
                state.list = [];
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = action.payload.data || action.payload;
                state.pagination = action.payload.pagination || state.pagination;
                state.error = null;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
                state.list = [];
            })

            .addCase(fetchPastOrders.pending, (state) => {
                state.pastStatus = 'loading';
                state.pastList = [];
            })
            .addCase(fetchPastOrders.fulfilled, (state, action) => {
                state.pastStatus = 'success';
                state.pastList = action.payload.data || action.payload;
                state.pastPagination = action.payload.pagination || state.pastPagination;
            })
            .addCase(fetchPastOrders.rejected, (state, action) => {
                state.pastStatus = 'failed';
                state.error = action.error.message || 'An error occurred';
                state.pastList = [];
            })

            .addCase(fetchHomepageOrders.pending, (state) => {
                state.status = 'loading';
                state.homepageList = null;
            })
            .addCase(fetchHomepageOrders.fulfilled, (state, action) => {
                state.status = 'success';
                state.homepageList = action.payload;
                state.error = null;
            })
            .addCase(fetchHomepageOrders.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
                state.homepageList = null;
            })

            .addCase(fetchHomepageOrganizations.pending, (state) => {
                state.status = 'loading';
                state.homepageOrganizations = null;
            })
            .addCase(fetchHomepageOrganizations.fulfilled, (state, action) => {
                state.status = 'success';
                state.homepageOrganizations = action.payload;
                state.error = null;
            })
            .addCase(fetchHomepageOrganizations.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
                state.homepageOrganizations = null;
            })

            .addCase(fetchOrder.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchOrder.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;
                state.error = null;
            })
            .addCase(fetchOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(orderPurchase.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(orderPurchase.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;

                // Update the order in the list if it exists, otherwise add it
                const orderIndex = state.list.findIndex(order => order._id === action.payload._id);
                if (orderIndex !== -1) {
                    state.list[orderIndex] = action.payload;
                } else {
                    state.list.unshift(action.payload);
                }

                state.error = null;
            })
            .addCase(orderPurchase.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || action.error.message || 'An error occurred';
            })

            .addCase(orderRevert.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(orderRevert.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;
                state.error = null;
            })
            .addCase(orderRevert.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(amendScreeningDate.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;
                const orderIndex = state.list.findIndex(order => order._id === action.payload._id);
                if (orderIndex !== -1) {
                    state.list[orderIndex] = action.payload;
                }
                state.error = null;
            })
            .addCase(amendScreeningDate.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(updateOrder.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateOrder.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;
                state.error = null;
            })
            .addCase(updateOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(requestApproval.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(requestApproval.fulfilled, (state, action) => {
                state.status = 'success';
                state.currentOrder = action.payload;

                // Update the order in the list
                const orderIndex = state.list.findIndex(order => order._id === action.payload._id);
                if (orderIndex !== -1) {
                    state.list[orderIndex] = action.payload;
                }

                state.error = null;
            })
            .addCase(requestApproval.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(getIngestJobs.pending, (state) => {
                state.statusIngest = 'loading';
            })
            .addCase(getIngestJobs.fulfilled, (state, action) => {
                state.statusIngest = 'success';
                state.ingestJobs = action.payload?.ingestJobs || [];
                state.error = null;
            })
            .addCase(getIngestJobs.rejected, (state, action) => {
                state.statusIngest = 'failed';
                state.error = action.error.message || 'An error occurred';
            })
        ;
    },
});

export const { setCurrentOrder, markOrderFinishedPlayback, setOrderRealtimeStatus } = orderSlice.actions;
export default orderSlice.reducer;
